import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTopInfoBarController } from 'src/hooks/useTopInfoBarController';
import { DismissContainer, InfoBarContainer, topInfoBarTransitionTime, VERTICAL_PADDING } from './InfoBar.styles';
import Button from 'atoms/Button/Button';
import XClose from 'app/icons/Monospaced/X/XClose';
import { cssVar } from 'styles/helper';
import useElementResize from 'src/hooks/useElementResize';
import InactiveNetworkLayout from './Layouts/InactiveNetwork';
import PeopleMagicProfitAnnouncementBar from './Layouts/PeopleMagicProfitAnnouncementBar';
import PriceChangesAnnouncementLayout from './Layouts/PriceChangesAnnouncement';

export const TopBarInfoLayoutList = {
  inactiveNetworks: InactiveNetworkLayout,
  peopleMagicProfitAnnouncement: PeopleMagicProfitAnnouncementBar,
  priceChangesAnnouncement: PriceChangesAnnouncementLayout,
};

export type TopBarInfoLayouts = keyof typeof TopBarInfoLayoutList;

const InfoBar = (): JSX.Element => {
  const topBarRef = useRef<HTMLDivElement>();

  const {
    visible,
    contentLayout,
    hide,
    dismissible,
    backgroundColor = 'bg-primary-solid',
    dismissButtonColor = 'fg-white',
    customStyles,
  } = useTopInfoBarController();

  // Used for close transition
  const [visibility, setVisibility] = useState<'hidden' | 'visible' | 'closing'>('hidden');

  const InfoContent = TopBarInfoLayoutList[contentLayout as TopBarInfoLayouts];

  useEffect(() => {
    if (visible) {
      setVisibility('visible');
    } else {
      setVisibility('hidden');
    }
  }, [visible, InfoContent]);

  const isHidden = visibility === 'hidden' || visibility === 'closing';

  const { height } = useElementResize(topBarRef);

  const handleHide = () => {
    setVisibility('closing');

    setTimeout(() => {
      hide();
    }, topInfoBarTransitionTime);
  };

  const containerDisplay = useMemo(() => {
    if (visibility === 'hidden') return 'none';
    if (visibility === 'closing') return 'flex';

    return 'flex';
  }, [visibility]);

  const marginTopToHide = `calc(-${height}px - (2 * ${cssVar(VERTICAL_PADDING)}))`;

  return (
    <InfoBarContainer
      ref={topBarRef}
      $background={backgroundColor}
      $dismissible={dismissible}
      $customStyles={customStyles}
      id='info_bar'
      className='info-bar'
      style={{
        marginTop: isHidden ? marginTopToHide : 0,
        display: containerDisplay,
      }}
    >
      {InfoContent && <InfoContent />}
      {dismissible && (
        <DismissContainer data-color-mode='dark'>
          <Button
            size='md'
            variant='tertiary'
            onClick={handleHide}
            icon={
              <XClose
                width={20}
                color={cssVar(dismissButtonColor)}
              />
            }
            iconPosition='right'
          />
        </DismissContainer>
      )}
    </InfoBarContainer>
  );
};

export default InfoBar;
